import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/auth";
import { useI18n } from "@/i18n";
import { BACKEND } from "@/lib/api";
import { Card, PageTitle, Btn, Input, Select, Badge, Spinner, Empty, Modal, Field, fmtMoney, fmtDate } from "@/components/ui-rp";

const CashoutAlerts = ({ id, client, t, lang }) => {
  const [items, setItems] = useState(null);
  useEffect(() => { client.get(`/tenant/cashouts/${id}/alerts`).then(({ data }) => setItems(data)).catch(() => setItems([])); }, [client, id]);
  if (!items) return <Spinner />;
  return (
    <ul className="mt-3 border-t border-border pt-2 space-y-1 text-xs" data-testid={`cashout-alerts-list-${id}`}>
      {items.length === 0 ? <li className="text-muted-foreground">{t("balance.noAlerts")}</li> : items.map((e) => (
        <li key={e.id} className="flex justify-between gap-2"><span className="truncate">{e.to} <span className="text-muted-foreground">· {e.kind.replace("cashout_", "")}</span></span><span className="text-muted-foreground shrink-0">{e.status} · {fmtDate(e.created_at, lang)}</span></li>
      ))}
      <li className="text-[11px] text-muted-foreground">{t("tx.mockMail")}</li>
    </ul>
  );
};

export const BalancePage = () => {
  const { t, lang } = useI18n();
  const { client, user } = useAuth();
  const [bal, setBal] = useState(null);
  const [cashouts, setCashouts] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ amount: "", bank_account: "", totp_code: "" });
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [alertsFor, setAlertsFor] = useState(null);
  const load = useCallback(() => {
    client.get("/tenant/balance").then(({ data }) => setBal(data));
    client.get("/tenant/cashouts").then(({ data }) => setCashouts(data));
    if (user.role !== "cashier") client.get("/tenant/ledger").then(({ data }) => setLedger(data)).catch(() => {});
  }, [client, user.role]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await client.post("/tenant/cashouts", { ...form, amount: Number(form.amount) });
      setShow(false);
      setMsg(t("balance.requested"));
      setForm({ amount: "", bank_account: "", totp_code: "" });
      load();
    } catch (err) {
      setMsg(err.response?.data?.detail?.message || t("common.error"));
    } finally {
      setBusy(false);
    }
  };
  if (!bal) return <Spinner testId="balance-loading" />;
  const metrics = [["available", bal.available], ["pending", bal.pending], ["reserved", bal.reserved], ["earned", bal.earned]];
  return (
    <div className="space-y-6" data-testid="balance-page">
      <PageTitle testId="balance-title" action={user.role === "admin" && <Btn onClick={() => setShow(true)} testId="request-cashout-button">{t("balance.request")}</Btn>}>{t("balance.title")}</PageTitle>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {metrics.map(([k, v]) => (
          <Card key={k} testId={`balance-${k}`}><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t(`balance.${k}`)}</p><p className="mt-2 font-heading text-2xl font-extrabold">{fmtMoney(v)}</p></Card>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{t("balance.split")}</p>
      {msg && <p className="text-sm font-medium" data-testid="cashout-message">{msg}</p>}
      <section className="space-y-2">
        <h2 className="font-heading text-base md:text-lg font-semibold">{t("balance.history")}</h2>
        {cashouts.length === 0 ? <Empty testId="cashouts-empty">{t("balance.empty")}</Empty> : cashouts.map((c) => (
          <Card key={c.id} testId={`cashout-row-${c.id}`} className="py-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div><p className="font-bold">{fmtMoney(c.amount)}</p><p className="text-xs text-muted-foreground">{c.bank_account} · {fmtDate(c.created_at, lang)}{c.note ? ` · ${c.note}` : ""}</p></div>
              <div className="flex items-center gap-2"><Badge status={c.status} label={t(`status.${c.status}`)} />
                {c.status !== "requested" && <button type="button" onClick={() => setAlertsFor(alertsFor === c.id ? null : c.id)} data-testid={`cashout-alerts-${c.id}`} className="text-xs underline text-muted-foreground">{t("balance.alerts")}</button>}</div>
            </div>
            {alertsFor === c.id && <CashoutAlerts id={c.id} client={client} t={t} lang={lang} />}
          </Card>
        ))}
      </section>
      {user.role !== "cashier" && (
        <section className="space-y-2">
          <h2 className="font-heading text-base md:text-lg font-semibold">{t("balance.ledger")}</h2>
          {ledger.length === 0 ? <Empty testId="ledger-empty">{t("balance.empty")}</Empty> : (
            <div className="bg-card rounded-2xl border border-border overflow-x-auto">
              <table className="w-full text-sm min-w-[520px]">
                <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border"><th className="px-4 py-3">{t("tx.order")}</th><th className="px-4 py-3">{t("balance.account")}</th><th className="px-4 py-3 text-right">{t("balance.debit")}</th><th className="px-4 py-3 text-right">{t("balance.credit")}</th></tr></thead>
                <tbody>
                  {ledger.map((e) => e.lines.map((l, i) => (
                    <tr key={`${e.id}-${i}`} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{i === 0 ? `${e.order_number} · ${e.kind}` : ""}</td>
                      <td className="px-4 py-2 text-xs">{l.account}</td>
                      <td className="px-4 py-2 text-right font-mono">{l.debit ? fmtMoney(l.debit) : ""}</td>
                      <td className="px-4 py-2 text-right font-mono">{l.credit ? fmtMoney(l.credit) : ""}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      {show && (
        <Modal title={t("balance.request")} onClose={() => setShow(false)} testId="cashout-modal">
          <form onSubmit={submit} className="space-y-3">
            <Field label={t("balance.amount")}><Input testId="cashout-amount-input" type="number" step="0.01" min="0.01" max={bal.available} required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
            <Field label={t("balance.bank")}><Input testId="cashout-bank-input" required value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} /></Field>
            <Field label={t("balance.totp")}><Input testId="cashout-totp-input" inputMode="numeric" maxLength={6} required value={form.totp_code} onChange={(e) => setForm({ ...form, totp_code: e.target.value.replace(/\D/g, "") })} className="text-center font-mono tracking-[0.4em]" /></Field>
            {msg && <p className="text-sm text-destructive">{msg}</p>}
            <Btn type="submit" busy={busy} testId="cashout-submit" className="w-full h-12">{t("balance.request")}</Btn>
          </form>
        </Modal>
      )}
    </div>
  );
};

export const ApiKeysPage = () => {
  const { t, lang } = useI18n();
  const { client } = useAuth();
  const [keys, setKeys] = useState(null);
  const [name, setName] = useState("");
  const [secret, setSecret] = useState("");
  const load = useCallback(() => client.get("/tenant/api-keys").then(({ data }) => setKeys(data)).catch(() => setKeys([])), [client]);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    const { data } = await client.post("/tenant/api-keys", { name });
    setSecret(data.secret);
    setName("");
    load();
  };
  const revoke = async (k) => { await client.delete(`/tenant/api-keys/${k.id}`); load(); };
  return (
    <div className="space-y-4" data-testid="apikeys-page">
      <PageTitle testId="apikeys-title">{t("keys.title")}</PageTitle>
      <p className="text-sm text-muted-foreground">{t("keys.usage")}</p>
      <form onSubmit={create} className="flex flex-col sm:flex-row gap-2 max-w-lg">
        <Input testId="apikey-name-input" placeholder={t("keys.name")} required value={name} onChange={(e) => setName(e.target.value)} />
        <Btn type="submit" testId="apikey-create-button" className="h-12">{t("keys.create")}</Btn>
      </form>
      {secret && (
        <Card testId="apikey-secret-card" className="border-primary/50">
          <p className="text-sm font-semibold mb-2">{t("keys.secretOnce")}</p>
          <code className="block text-xs font-mono break-all bg-secondary rounded-lg p-3" data-testid="apikey-secret-value">{secret}</code>
        </Card>
      )}
      {!keys ? <Spinner /> : keys.length === 0 ? <Empty testId="apikeys-empty">{t("keys.empty")}</Empty> : keys.map((k) => (
        <Card key={k.id} testId={`apikey-row-${k.id}`} className="py-3 flex items-center justify-between gap-3 flex-wrap">
          <div><p className="font-semibold">{k.name} <span className="font-mono text-xs text-muted-foreground">{k.prefix}…</span></p><p className="text-xs text-muted-foreground">{t("keys.lastUsed")}: {fmtDate(k.last_used_at, lang)}</p></div>
          <div className="flex items-center gap-2"><Badge status={k.status === "active" ? "active" : "disabled"} label={k.status} />{k.status === "active" && <Btn variant="danger" className="h-9 px-3 text-xs" onClick={() => revoke(k)} testId={`apikey-revoke-${k.id}`}>{t("keys.revoke")}</Btn>}</div>
        </Card>
      ))}
    </div>
  );
};

export const WebhooksPage = () => {
  const { t, lang } = useI18n();
  const { client } = useAuth();
  const [hooks, setHooks] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [url, setUrl] = useState("");
  const [result, setResult] = useState("");
  const load = useCallback(() => {
    client.get("/tenant/webhooks").then(({ data }) => setHooks(data)).catch(() => setHooks([]));
    client.get("/tenant/webhooks/deliveries").then(({ data }) => setDeliveries(data)).catch(() => {});
  }, [client]);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => { e.preventDefault(); await client.post("/tenant/webhooks", { url, events: ["*"] }); setUrl(""); load(); };
  const test = async (h) => { const { data } = await client.post(`/tenant/webhooks/${h.id}/test`); setResult(data.ok ? t("hooks.testOk") : `${t("hooks.testFail")} (${data.status_code || "—"})`); load(); };
  const del = async (h) => { await client.delete(`/tenant/webhooks/${h.id}`); load(); };
  return (
    <div className="space-y-4" data-testid="webhooks-page">
      <PageTitle testId="webhooks-title">{t("hooks.title")}</PageTitle>
      <p className="text-sm text-muted-foreground">{t("hooks.sig")}</p>
      <form onSubmit={create} className="flex flex-col sm:flex-row gap-2 max-w-xl">
        <Input testId="webhook-url-input" type="url" placeholder={t("hooks.url")} required value={url} onChange={(e) => setUrl(e.target.value)} />
        <Btn type="submit" testId="webhook-create-button" className="h-12">{t("hooks.create")}</Btn>
      </form>
      {result && <p className="text-sm font-medium" data-testid="webhook-test-result">{result}</p>}
      {!hooks ? <Spinner /> : hooks.length === 0 ? <Empty testId="webhooks-empty">{t("hooks.empty")}</Empty> : hooks.map((h) => (
        <Card key={h.id} testId={`webhook-row-${h.id}`} className="space-y-2">
          <p className="font-mono text-sm break-all">{h.url}</p>
          <p className="text-xs text-muted-foreground">{t("hooks.secret")}: <code className="font-mono">{h.secret}</code></p>
          <div className="flex gap-2"><Btn variant="outline" className="h-9 px-3 text-xs" onClick={() => test(h)} testId={`webhook-test-${h.id}`}>{t("hooks.test")}</Btn><Btn variant="danger" className="h-9 px-3 text-xs" onClick={() => del(h)} testId={`webhook-delete-${h.id}`}>{t("hooks.delete")}</Btn></div>
        </Card>
      ))}
      {deliveries.length > 0 && (
        <section className="space-y-2"><h2 className="font-heading text-base md:text-lg font-semibold">{t("hooks.deliveries")}</h2>
          {deliveries.map((d) => (<Card key={d.id} className="py-2 text-xs flex justify-between gap-2 flex-wrap"><span className="font-mono">{d.event}</span><span className={d.ok ? "text-[hsl(122,87%,30%)] dark:text-primary" : "text-destructive"}>{d.status_code || "ERR"}</span><span className="text-muted-foreground">{fmtDate(d.created_at, lang)}</span></Card>))}
        </section>
      )}
    </div>
  );
};

export const ButtonPage = () => {
  const { t } = useI18n();
  const { client, user } = useAuth();
  const [links, setLinks] = useState([]);
  const [link, setLink] = useState("");
  const [mode, setMode] = useState("redirect");
  const [label, setLabel] = useState("");
  const [copied, setCopied] = useState(false);
  useEffect(() => { client.get("/tenant/links").then(({ data }) => { setLinks(data); if (data[0]) setLink(data[0].link_id); }); }, [client]);
  const slug = user.tenant?.slug;
  const code = `<script src="${BACKEND}/api/v2/public/button.js" data-base="${BACKEND}" defer></script>\n<button data-rapidpay-button data-slug="${slug}" data-link="${link}" data-mode="${mode}"${label ? ` data-label="${label}"` : ""}></button>`;
  const css = `:root{\n  --rp-primary:#0fe814;\n  --rp-on-primary:#0b0f19;\n  --rp-radius:999px;\n  --rp-font:inherit;\n}`;
  return (
    <div className="space-y-4" data-testid="button-page">
      <PageTitle testId="button-title">{t("button.title")}</PageTitle>
      <p className="text-sm text-muted-foreground max-w-2xl">{t("button.intro")}</p>
      {links.length === 0 ? <Empty testId="button-no-links">{t("button.noLinks")}</Empty> : (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="space-y-3">
            <Field label={t("button.link")}><Select testId="button-link-select" value={link} onChange={(e) => setLink(e.target.value)}>{links.map((l) => <option key={l.link_id} value={l.link_id}>{l.title}</option>)}</Select></Field>
            <Field label={t("button.mode")}><Select testId="button-mode-select" value={mode} onChange={(e) => setMode(e.target.value)}><option value="redirect">{t("button.redirect")}</option><option value="modal">{t("button.modal")}</option></Select></Field>
            <Field label={t("button.label")}><Input testId="button-label-input" value={label} onChange={(e) => setLabel(e.target.value)} /></Field>
            <div><p className="text-sm font-medium mb-2">{t("button.preview")}</p>
              <a href={`/pagar/${slug}/${link}`} target="_blank" rel="noreferrer" data-testid="button-preview" className="inline-flex items-center h-12 px-7 rounded-full bg-primary text-primary-foreground font-bold">{label || (user.language === "en" ? "Pay with RapidPay" : "Pagar con RapidPay")}</a></div>
          </Card>
          <Card className="space-y-3">
            <div className="flex items-center justify-between"><p className="text-sm font-medium">{t("button.code")}</p><Btn variant="outline" className="h-9 px-3 text-xs" onClick={() => { navigator.clipboard?.writeText(code); setCopied(true); }} testId="button-copy-code">{copied ? t("tx.copied") : t("button.copy")}</Btn></div>
            <pre className="text-xs font-mono bg-secondary rounded-xl p-3 overflow-x-auto whitespace-pre-wrap break-all" data-testid="button-code">{code}</pre>
            <p className="text-sm font-medium">{t("button.cssTitle")}</p><p className="text-xs text-muted-foreground">{t("button.cssHelp")}</p>
            <pre className="text-xs font-mono bg-secondary rounded-xl p-3 overflow-x-auto">{css}</pre>
            <p className="text-xs text-muted-foreground">{t("button.events")}</p>
          </Card>
        </div>
      )}
    </div>
  );
};

export const SettingsPage = () => {
  const { t } = useI18n();
  const { client, user } = useAuth();
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  const [own, setOwn] = useState(null);
  const [vals, setVals] = useState({});
  const load = useCallback(() => client.get("/tenant/settings").then(({ data }) => setS(data)).catch(() => {}), [client]);
  useEffect(() => { load(); }, [load]);
  if (!s) return <Spinner testId="settings-loading" />;
  const readOnly = user.role !== "admin";
  const save = async (patch) => { const { data } = await client.patch("/tenant/settings", patch); setS(data); setMsg(t("settings.saved")); setTimeout(() => setMsg(""), 1500); };
  const fields = { cybersource: ["CYBS_SA_ACCESS_KEY", "CYBS_SA_PROFILE_ID", "CYBS_SA_SECRET_KEY", "CYBS_MERCHANT_ID", "CYBS_REST_KEY_ID", "CYBS_REST_SHARED_SECRET"], yappy: ["YAPPY_MERCHANT_ID", "YAPPY_SECRET_KEY", "YAPPY_DOMAIN_URL"] };
  const saveOwn = async (e) => { e.preventDefault(); await client.put("/tenant/settings/credentials", { provider: own, values: vals }); setOwn(null); setVals({}); load(); };
  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageTitle testId="settings-title">{t("settings.title")}</PageTitle>
      {msg && <p className="text-sm font-medium text-[hsl(122,87%,30%)] dark:text-primary" data-testid="settings-saved">{msg}</p>}
      <Card className="space-y-3">
        <h2 className="font-heading text-base md:text-lg font-semibold">{t("settings.methods")}</h2>
        {["card", "yappy"].map((m) => (
          <label key={m} className="flex items-center justify-between gap-3 min-h-[44px]"><span className="text-sm">{t(`settings.${m}`)}</span>
            <input type="checkbox" data-testid={`settings-method-${m}`} disabled={readOnly} checked={!!s.payment_methods[m]} onChange={(e) => save({ payment_methods: { ...s.payment_methods, [m]: e.target.checked } })} className="w-5 h-5 accent-[#0fe814]" /></label>
        ))}
      </Card>
      <Card className="space-y-3">
        <h2 className="font-heading text-base md:text-lg font-semibold">{t("settings.branding")}</h2>
        {[["display_name", t("settings.displayName")], ["color", t("settings.color")], ["logo_url", t("settings.logoUrl")]].map(([k, label]) => (
          <Field key={k} label={label}><Input testId={`settings-branding-${k}`} disabled={readOnly} defaultValue={s.branding?.[k] || ""} onBlur={(e) => !readOnly && e.target.value !== (s.branding?.[k] || "") && save({ branding: { ...s.branding, [k]: e.target.value } })} /></Field>
        ))}
        <Field label={t("settings.notify")}><Input testId="settings-notify-email" type="email" disabled={readOnly} defaultValue={s.notification_email || ""} onBlur={(e) => !readOnly && save({ notification_email: e.target.value })} /></Field>
      </Card>
      <Card className="space-y-3" testId="settings-fees">
        <h2 className="font-heading text-base md:text-lg font-semibold">{t("settings.fees")}</h2>
        <p className="text-xs text-muted-foreground">{t("settings.feesHelp")}</p>
        <ul className="text-sm divide-y divide-border">
          {Object.entries(s.fees || {}).map(([k, v]) => (
            <li key={k} className="py-2 flex justify-between gap-3"><span>{t(`admin.feeNames.${k}`)}</span><span className="font-mono">{Number(v.pct).toFixed(2)}% + ${Number(v.fixed).toFixed(2)}</span></li>
          ))}
        </ul>
      </Card>
      <Card className="space-y-3">
        <h2 className="font-heading text-base md:text-lg font-semibold">{t("settings.creds")}</h2>
        <p className="text-xs text-muted-foreground">{t("settings.credsHelp")}</p>
        {["cybersource", "yappy"].map((p) => (
          <div key={p} className="flex items-center justify-between gap-3 flex-wrap min-h-[44px]" data-testid={`settings-creds-${p}`}>
            <span className="text-sm capitalize font-medium">{p} <span className="text-muted-foreground font-normal">· {t(`settings.mode.${s.credentials[p]}`)}</span></span>
            {!readOnly && (s.credentials[p] === "own"
              ? <Btn variant="outline" className="h-9 px-3 text-xs" onClick={async () => { await client.delete(`/tenant/settings/credentials/${p}`); load(); }} testId={`settings-creds-remove-${p}`}>{t("settings.removeOwn")}</Btn>
              : <Btn variant="outline" className="h-9 px-3 text-xs" onClick={() => setOwn(p)} testId={`settings-creds-own-${p}`}>{t("settings.useOwn")}</Btn>)}
          </div>
        ))}
      </Card>
      {own && (
        <Modal title={`${t("settings.useOwn")} · ${own}`} onClose={() => setOwn(null)} testId="own-creds-modal">
          <form onSubmit={saveOwn} className="space-y-3">
            {fields[own].map((k) => <Field key={k} label={k}><Input testId={`own-creds-${k}`} type={/SECRET|KEY/.test(k) && !/ACCESS|KEY_ID/.test(k) ? "password" : "text"} required value={vals[k] || ""} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} /></Field>)}
            <Btn type="submit" testId="own-creds-submit" className="w-full h-12">{t("common.save")}</Btn>
          </form>
        </Modal>
      )}
    </div>
  );
};
